import type {ConnectElementConfig} from '../../../connect/ConnectJSInterface/ConnectElementList';
import {ComponentOverrideFlag} from '../../../utils/flags/flags';

export const layoutParameter = 'layout' as const;
export const privacyPolicyUrlParameter = 'privacy-policy-url' as const;
export const eligibilityCriteriaUrlParameter =
  'eligibility-criteria-url' as const;
export const howCapitalWorksUrlParameter = 'how-capital-works-url' as const;
export const applicationPresentationParameter =
  'application-presentation' as const;
export const capitalSchemeParameter = 'capital-scheme' as const;
export const supportUrlParameter = 'support-url' as const;
export const enumDisableViewOfferButtonParameter =
  'enum-disable-view-offer-button' as const;
export const debugParameter = 'debug' as const;
export const disableNestedCapitalFinancingApplicationParameter =
  'disable-nested-capital-financing-application' as const;

export const CapitalFinancingPromotionConfig: ConnectElementConfig = {
  Component: () =>
    import(
      /* webpackChunkName: "capitalFinancingPromotionExperience" */
      '../../../components/Capital/CapitalFinancingPromotion'
    ),
  enabledOverrideFlag: ComponentOverrideFlag.capital_financing_promotion,
  htmlAttributes: {
    [layoutParameter]: {
      isInternal: false,
    },
    [privacyPolicyUrlParameter]: {
      isInternal: false,
    },
    [eligibilityCriteriaUrlParameter]: {
      isInternal: false,
    },
    [howCapitalWorksUrlParameter]: {
      isInternal: false,
    },
    // Internal - API reviewed but in development
    [disableNestedCapitalFinancingApplicationParameter]: {
      isInternal: true,
    },
    // Internal - to enable these for platforms, we'll need API review
    [enumDisableViewOfferButtonParameter]: {
      isInternal: true,
    },
    [supportUrlParameter]: {
      isInternal: true,
    },
    [applicationPresentationParameter]: {
      isInternal: true,
    },
    [capitalSchemeParameter]: {
      isInternal: true,
    },
    [debugParameter]: {
      isInternal: true,
    },
  },
  htmlEvents: [
    'onapplicationsubmitted',
    'oneligiblefinancingofferloaded',
    'onapplicationstepchange',
    'onadvancetocapitalfinancingapplication',
  ],
  customHtmlElementMethods: {
    setLayout: {
      type: 'AttributeSettingMethod',
      attribute: layoutParameter,
      isInternal: false,
      description:
        'Sets the layout mode of the component. Valid options are "banner" or "full".',
    },
    setPrivacyPolicyUrl: {
      type: 'AttributeSettingMethod',
      attribute: privacyPolicyUrlParameter,
      isInternal: false,
      description:
        "Customizes the URL used for the Privacy Policy link in the application component. If not set, will default to Stripe's Privacy Policy.",
    },
    setEligibilityCriteriaUrl: {
      type: 'AttributeSettingMethod',
      attribute: eligibilityCriteriaUrlParameter,
      isInternal: false,
      description:
        "Customizes the URL used for links to eligibility criteria documentation in the promotion component. If not set, will default to Stripe Capital's Eligibility Criteria.",
    },
    setHowCapitalWorksUrl: {
      type: 'AttributeSettingMethod',
      attribute: howCapitalWorksUrlParameter,
      isInternal: false,
      description:
        "Customizes the URL used for links to documentation about the Capital program in the promotion component. If not set, will default to Stripe Capital's documentation.",
    },
    setOnApplicationSubmitted: {
      type: 'EventListenerMethod',
      event: 'onapplicationsubmitted',
      isInternal: false,
      description:
        'Sets the event listener that allows a user to pass in a callback function for when a financing application is submitted.',
    },
    setOnApplicationStepChange: {
      type: 'EventListenerMethod',
      event: 'onapplicationstepchange',
      isInternal: false,
      description: 'Sets a callback when the application step is navigated',
    },
    setOnEligibleFinancingOfferLoaded: {
      type: 'EventListenerMethod',
      event: 'oneligiblefinancingofferloaded',
      isInternal: false,
      description:
        'Sets the event listener that allows a user to pass in a callback function for when a financing offer is loaded.',
    },
    // Internal - API reviewed but in development
    setOnAdvanceToCapitalFinancingApplication: {
      type: 'EventListenerMethod',
      event: 'onadvancetocapitalfinancingapplication',
      isInternal: true,
      description:
        'Sets a callback that fires when the user clicks the "View offer" CTA which opens the application',
    },
    // Internal - API reviewed but in development
    setDisableNestedCapitalFinancingApplication: {
      type: 'AttributeSettingMethod',
      attribute: disableNestedCapitalFinancingApplicationParameter,
      isInternal: true,
      description:
        'When set to true, disables opening the application overlay upon clicking the "View offer" button. Intended for use with onAdvanceToCapitalFinancingApplication.',
    },
    // Internal - to enable these for platforms, we'll need API review
    setEnumDisableViewOfferButton: {
      type: 'AttributeSettingMethod',
      attribute: enumDisableViewOfferButtonParameter,
      isInternal: true,
      description: 'Sets the reason why the "View offer" button is disabled.',
    },
    setSupportUrl: {
      type: 'AttributeSettingMethod',
      attribute: supportUrlParameter,
      isInternal: true,
      description: 'Sets the support url to display',
    },
    setApplicationPresentation: {
      type: 'AttributeSettingMethod',
      attribute: applicationPresentationParameter,
      isInternal: true,
      description:
        "Sets how the offer application is presented upon clicking 'Start application'",
    },
    setCapitalScheme: {
      type: 'AttributeSettingMethod',
      attribute: capitalSchemeParameter,
      isInternal: true,
      description:
        'Sets the CapitalScheme value, used to indicate when the component is rendering in certain Stripe-owned surfaces.',
    },
  },
  releaseState: 'preview',
  sentryTeam: 'capital_core_products',
  teamOwner: 'capital-core-products',
  description:
    "Displays information about the merchant's eligible financing offers and can open the application form.",
  friendlyName: 'Capital financing promotion',
  docSitePath:
    '/connect/supported-embedded-components/capital-financing-promotion',
  syntheticTestInfo: {
    testUrl:
      'https://amp.corp.stripe.com/synthetics/us-west-2/embedded-flex-loan-individual',
  },
};
